/**
 * AuthGate — Phase 1 sign-in splash + Phase C local-auth router.
 *
 * Decision tree on mount:
 *
 *   1. URL is /reset-password?token=… → render <ChangePasswordScreen
 *      resetToken={…}/>. Token-mode bypasses every other check because
 *      the user is signed-out by definition and the URL itself proves
 *      identity.
 *
 *   2. GET /api/v1/auth/local/capabilities
 *      - 200 → backend is 'local'.
 *      - 401/404/anything-else → backend is 'workos'.
 *
 *   3. (local only) GET /api/v1/setup/status
 *      - { first_run: true } → render <SetupWizardScreen/>.
 *      - { first_run: false } → continue.
 *
 *   4. POST /api/v1/auth/me
 *      - 200 with must_change_password=true → render
 *        <ChangePasswordScreen forceChange/>.
 *      - 200 → render <App/>.
 *      - 401 (workos) → render WorkOS SignInSplash (AuthKit redirect).
 *      - 401 (local)  → render LocalSignInForm.
 *
 * WorkOS deployments only ever reach branches 1, 4-workos. Local
 * deployments use 1, 2, 3, 4-local. The two trees never cross.
 */

import { useEffect, useRef, useState } from 'react';
import {
  App,
  ChangePasswordScreen,
  SetupWizardScreen,
  SplashScreen,
  useDebugRegister,
  debugLog,
} from '@cipherops/ui';
import { getDataSource, IpcError, PRODUCT_NAME } from '@cipherops/data-source';
import { SignupVerifyPage, SignupPage, BillingResumePage, BillingRestorePage } from './PublicPages';

interface AuthMe {
  user_id: string;
  firm_id: string;
  email: string;
  display_name: string;
  role: 'firm_admin' | 'senior_consultant' | 'consultant' | 'viewer' | 'resource_manager';
  auth_provider?: 'workos' | 'local';
  must_change_password?: boolean;
}

interface LocalCapabilities {
  backend: 'local';
  smtp_enabled: boolean;
  self_service_reset: boolean;
  admin_email_reset: boolean;
  admin_link_reset: boolean;
  admin_temp_password: boolean;
}

interface SetupStatus {
  first_run: boolean;
}

type Backend = 'workos' | 'local';

type AuthState =
  | { status: 'loading' }
  | { status: 'workos-signed-out' }
  | { status: 'local-signed-out'; capabilities: LocalCapabilities }
  | { status: 'first-run' }
  | { status: 'force-change'; user: AuthMe }
  | { status: 'signed-in'; user: AuthMe }
  | { status: 'signed-in-change-password'; user: AuthMe }
  | { status: 'reset-token'; token: string }
  | { status: 'public-signup-verify' }
  | { status: 'public-billing-resume' }
  | { status: 'public-billing-restore' }
  | { status: 'public-signup' }
  | { status: 'error'; code: string; message: string };

/** Read ?token= from a /reset-password URL. Returns null if not on that route. */
function extractResetToken(): string | null {
  const path = window.location.pathname;
  if (path !== '/reset-password') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  return token && token.length > 0 ? token : null;
}

async function detectBackend(): Promise<{ backend: Backend; capabilities: LocalCapabilities | null }> {
  // /capabilities is local-only — 200 → local, anything else → workos.
  // We fetch directly with fetch() (not via the data-source) so we
  // don't pollute the API-call ledger with a probe on every reload.
  try {
    const r = await fetch('/api/v1/auth/local/capabilities', {
      credentials: 'include',
    });
    if (r.ok) {
      const capabilities = (await r.json()) as LocalCapabilities;
      return { backend: 'local', capabilities };
    }
  } catch {
    // Network failures fall through to workos default.
  }
  return { backend: 'workos', capabilities: null };
}

async function fetchSetupStatus(): Promise<SetupStatus | null> {
  try {
    const r = await fetch('/api/v1/setup/status', { credentials: 'include' });
    if (!r.ok) return null;
    return (await r.json()) as SetupStatus;
  } catch {
    return null;
  }
}

export function AuthGate() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  // Expose AuthGate's decision tree state to the Debug Panel. The user
  // (or anyone driving a support bundle) can see exactly which branch
  // resolved by opening the State tab on the panel.
  useDebugRegister('AuthGate', {
    status: auth.status,
    // Don't leak the full token in the registry — record just whether
    // one was present in the URL.
    backend:
      auth.status === 'local-signed-out'
        ? 'local'
        : auth.status === 'workos-signed-out'
          ? 'workos'
          : auth.status === 'signed-in'
            ? (auth as { user: { auth_provider?: string } }).user.auth_provider
            : undefined,
  });

  // A session can end BETWEEN requests, not just before one: the account is
  // deactivated, or the whole workspace is closed (reclaimed trial, cancelled
  // subscription, organisation deleted upstream). AuthGate only evaluates the
  // session on mount, so without this the shell stayed rendered around empty
  // screens and an inline "you must be signed in" error — which reads as
  // "still signed in, data missing" rather than "signed out". RemoteDataSource
  // raises this from the single place every API call passes through, so one
  // listener covers every screen.
  // Belt and braces with the channel filter in RemoteDataSource: react ONLY if
  // we were actually signed in. A 401 while already signed out is ordinary, and
  // reloading on it loops forever — which is exactly what shipped and what this
  // guard makes structurally impossible, wherever a stray 401 comes from.
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    const onSessionEnded = () => {
      const s = authRef.current.status;
      const hadSession =
        s === 'signed-in' || s === 'signed-in-change-password' || s === 'force-change';
      if (!hadSession) return;
      debugLog.recordEvent({
        type: 'authgate_session_ended',
        description: 'server ended the session mid-visit; dropping to sign-in',
      });
      // Re-run the normal evaluation rather than hand-rolling the signed-out
      // state here — it already picks the right sign-in surface per backend,
      // and duplicating that choice is how the two drift apart.
      window.location.reload();
    };
    window.addEventListener('cipherops:session-ended', onSessionEnded);
    return () => window.removeEventListener('cipherops:session-ended', onSessionEnded);
  }, []);

  useEffect(() => {
    debugLog.recordEvent({
      type: 'authgate_mounted',
      description: 'AuthGate evaluating session + backend',
    });
    // Branch 0 — PUBLIC token pages, pinned before anything else.
    //
    // Both are reached from an email by someone who cannot sign in: a new
    // customer confirming a signup, or a closed-out one returning to
    // subscribe. Evaluating the session first would bounce them to a sign-in
    // screen they have no way through, which is precisely the dead end these
    // pages exist to remove.
    const path = window.location.pathname;
    if (path.startsWith('/signup/verify')) {
      setAuth({ status: 'public-signup-verify' });
      return;
    }
    if (path.startsWith('/billing/resume')) {
      setAuth({ status: 'public-billing-resume' });
      return;
    }
    if (path.startsWith('/billing/restore')) {
      setAuth({ status: 'public-billing-restore' });
      return;
    }
    // AFTER the /signup/verify test above — '/signup' is a prefix of it, so
    // checking this first would swallow every verification link.
    if (path.startsWith('/signup')) {
      setAuth({ status: 'public-signup' });
      return;
    }

    // Branch 1 — /reset-password URL pinned before any network calls.
    const token = extractResetToken();
    if (token) {
      debugLog.recordEvent({
        type: 'authgate_branch_token',
        description: 'reset-token detected in URL; rendering token-mode change-password',
      });
      setAuth({ status: 'reset-token', token });
      return;
    }

    (async () => {
      // Branch 2 — backend detection.
      const { backend, capabilities } = await detectBackend();
      debugLog.recordEvent({
        type: 'authgate_backend_detected',
        description: `auth backend = ${backend}`,
        data: { backend, smtp_enabled: capabilities?.smtp_enabled ?? null },
      });

      // Branch 3 — local-only first-run check (before /me, because
      // /me is not allowed during first-run).
      if (backend === 'local') {
        const status = await fetchSetupStatus();
        if (status?.first_run) {
          debugLog.recordEvent({
            type: 'authgate_branch_first_run',
            description: 'first_run=true; rendering SetupWizardScreen',
          });
          setAuth({ status: 'first-run' });
          return;
        }
      }

      // Branch 4 — /me.
      try {
        const user = await getDataSource().invoke<AuthMe>('cipherops/auth/me');
        if (user.must_change_password) {
          debugLog.recordEvent({
            type: 'authgate_branch_force_change',
            description: 'must_change_password=true; rendering ChangePasswordScreen',
            data: { user_id: user.user_id, role: user.role },
          });
          setAuth({ status: 'force-change', user });
          return;
        }
        debugLog.recordEvent({
          type: 'authgate_branch_signed_in',
          description: `signed in as ${user.email} (${user.role})`,
          data: {
            user_id: user.user_id,
            firm_id: user.firm_id,
            role: user.role,
            auth_provider: user.auth_provider,
          },
        });
        // Voluntary change-password route — only available for local-auth.
        // WorkOS users have no /change-password page; the link in
        // ProfileScreen points at WorkOS portal instead.
        if (
          window.location.pathname === '/change-password' &&
          user.auth_provider === 'local'
        ) {
          setAuth({ status: 'signed-in-change-password', user });
          return;
        }
        setAuth({ status: 'signed-in', user });
      } catch (err) {
        if (err instanceof IpcError) {
          if (err.code === 'SESSION_REQUIRED' || err.code === 'HTTP_401') {
            if (backend === 'local' && capabilities) {
              debugLog.recordEvent({
                type: 'authgate_branch_local_signed_out',
                description: 'no session; rendering LocalSignInForm',
              });
              setAuth({ status: 'local-signed-out', capabilities });
            } else {
              debugLog.recordEvent({
                type: 'authgate_branch_workos_signed_out',
                description: 'no session; rendering WorkOSSignInSplash',
              });
              setAuth({ status: 'workos-signed-out' });
            }
            return;
          }
          debugLog.recordEvent({
            type: 'authgate_error',
            description: `unexpected IpcError ${err.code}: ${err.message}`,
            data: { code: err.code },
          });
          setAuth({ status: 'error', code: err.code, message: err.message });
          return;
        }
        const msg = err instanceof Error ? err.message : 'Unknown error';
        debugLog.recordEvent({
          type: 'authgate_error',
          description: `unexpected error: ${msg}`,
        });
        setAuth({ status: 'error', code: 'UNKNOWN', message: msg });
      }
    })();
  }, []);

  if (auth.status === 'loading') {
    return <Splash kind="loading" message="Checking your session…" />;
  }
  if (auth.status === 'public-signup-verify') {
    return <SignupVerifyPage />;
  }
  if (auth.status === 'public-billing-resume') {
    return <BillingResumePage />;
  }
  if (auth.status === 'public-billing-restore') {
    return <BillingRestorePage />;
  }
  if (auth.status === 'public-signup') {
    return <SignupPage />;
  }
  if (auth.status === 'reset-token') {
    return <ChangePasswordScreen resetToken={auth.token} onSuccessRedirect="/" />;
  }
  if (auth.status === 'first-run') {
    return <SetupWizardScreen onSuccessRedirect="/" />;
  }
  if (auth.status === 'force-change') {
    return <ChangePasswordScreen forceChange onSuccessRedirect="/" />;
  }
  if (auth.status === 'signed-in-change-password') {
    return <ChangePasswordScreen onSuccessRedirect="/profile" />;
  }
  if (auth.status === 'local-signed-out') {
    return <LocalSignInForm smtpEnabled={auth.capabilities.smtp_enabled} />;
  }
  if (auth.status === 'workos-signed-out') {
    return <WorkOSSignInSplash />;
  }
  if (auth.status === 'error') {
    return <Splash kind="error" message={`${auth.code}: ${auth.message}`} />;
  }
  // signed-in
  return <App />;
}

// ── Local-auth sign-in form ─────────────────────────────────────────────────

function LocalSignInForm({ smtpEnabled }: { smtpEnabled: boolean }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  useDebugRegister('LocalSignInForm', {
    has_email: email.length > 0,
    has_password: password.length > 0,
    submitting,
    forgot_sent: forgotSent,
    smtp_enabled: smtpEnabled,
    error,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    debugLog.recordEvent({
      type: 'local_signin_submitted',
      description: `sign-in attempt for ${email.trim().toLowerCase()}`,
    });
    try {
      const startedAt = performance.now();
      const r = await fetch('/api/v1/auth/local/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const durationMs = performance.now() - startedAt;
      debugLog.recordApiCall({
        channel: '/api/v1/auth/local/login',
        path: '/api/v1/auth/local/login',
        payload: { email: email.trim().toLowerCase(), password: '[REDACTED]' },
        ok: r.ok,
        status: r.status,
        durationMs,
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => null)) as { message?: string; code?: string } | null;
        debugLog.recordEvent({
          type: 'local_signin_failed',
          description: `${j?.code ?? `HTTP_${r.status}`}: ${j?.message ?? 'sign-in failed'}`,
          data: { code: j?.code, status: r.status },
        });
        setError(j?.message ?? `Sign-in failed (${r.status}).`);
        setSubmitting(false);
        return;
      }
      debugLog.recordEvent({
        type: 'local_signin_succeeded',
        description: 'session established; reloading',
      });
      // Full reload so AuthGate re-runs the decision tree with the
      // fresh session cookie.
      window.location.href = '/';
    } catch (err) {
      debugLog.recordEvent({
        type: 'local_signin_network_error',
        description: 'fetch failed before response',
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      setError('Could not reach the server. Try again.');
      setSubmitting(false);
    }
  }

  async function handleForgot() {
    if (!email.trim()) {
      setError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    setForgotBusy(true);
    setError(null);
    debugLog.recordEvent({
      type: 'forgot_password_submitted',
      description: `forgot-password request for ${email.trim().toLowerCase()}`,
    });
    try {
      const r = await fetch('/api/v1/auth/local/forgot', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      debugLog.recordApiCall({
        channel: '/api/v1/auth/local/forgot',
        path: '/api/v1/auth/local/forgot',
        payload: { email: email.trim().toLowerCase() },
        ok: r.ok,
        status: r.status,
        durationMs: 0,
      });
      // Server returns 200 unconditionally (no enumeration) — show the
      // confirmation regardless of whether the email was real.
      setForgotSent(true);
    } catch (err) {
      debugLog.recordEvent({
        type: 'forgot_password_network_error',
        description: 'fetch failed before response',
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      setError('Could not reach the server. Try again.');
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <main
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        fontFamily: 'var(--sans, system-ui, sans-serif)',
        background: 'var(--bg, #f6f4f0)',
      }}
    >
      <section
        style={{
          maxWidth: 420,
          width: '100%',
          background: 'var(--surface, #ffffff)',
          border: '0.5px solid var(--border-light, #e0ddd8)',
          padding: '40px 36px',
        }}
        data-debug-id="local-sign-in-card"
      >
        <div
          style={{
            fontFamily: 'var(--mono, "Courier New", monospace)',
            fontSize: 10,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: 'var(--text-muted, #7a7d8a)',
            marginBottom: 8,
            textAlign: 'center',
          }}
        >
          Welcome back
        </div>
        <h1
          style={{
            fontFamily: 'var(--serif, Georgia, serif)',
            fontSize: 28,
            fontWeight: 'normal',
            margin: '0 0 24px',
            textAlign: 'center',
          }}
        >
          {PRODUCT_NAME}<span style={{ color: 'var(--sev-high-label, #c44a1a)' }}>.</span>
        </h1>

        {forgotSent ? (
          <div data-debug-id="forgot-sent">
            <p style={{ fontSize: 13, lineHeight: 1.7 }}>
              If an account exists for <strong>{email}</strong>, a password-reset
              link has been emailed. Check your inbox (and your spam folder).
              The link expires in 1 hour.
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              No email? Contact your firm administrator to issue a reset
              link or temporary password directly.
            </p>
            <button
              type="button"
              onClick={() => {
                setForgotSent(false);
                setError(null);
              }}
              style={signInButtonStyle}
            >
              Back to sign-in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={labelStyle}>
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={submitting}
                required
                autoFocus
                data-debug-id="signin-email"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={submitting}
                required
                data-debug-id="signin-password"
                style={inputStyle}
              />
            </label>

            {error && (
              <div
                data-debug-id="signin-error"
                style={{
                  background: 'rgba(196, 74, 26, 0.08)',
                  borderLeft: '3px solid var(--sev-high-border, #c44a1a)',
                  color: 'var(--sev-high-label, #c44a1a)',
                  padding: '10px 12px',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !email || !password}
              data-debug-id="signin-submit"
              style={signInButtonStyle}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>

            {smtpEnabled && (
              <button
                type="button"
                onClick={handleForgot}
                disabled={forgotBusy}
                data-debug-id="signin-forgot"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent, #8b1a2e)',
                  fontSize: 12,
                  cursor: 'pointer',
                  marginTop: 4,
                  padding: 8,
                }}
              >
                {forgotBusy ? 'Sending…' : 'Forgot password?'}
              </button>
            )}
          </form>
        )}

        <p
          style={{
            marginTop: 24,
            fontSize: 11,
            color: 'var(--text-muted, #7a7d8a)',
            lineHeight: 1.7,
            paddingTop: 14,
            borderTop: '1px solid var(--border-light, #e0ddd8)',
            textAlign: 'center',
          }}
        >
          Self-hosted instance — passwords are managed locally.
          {!smtpEnabled && (
            <>
              <br />
              <em style={{ fontSize: 10.5 }}>
                SMTP not configured — contact your administrator if you've
                forgotten your password.
              </em>
            </>
          )}
          <br />
          <span style={{ fontSize: 10.5, marginTop: 6, display: 'inline-block' }}>
            Press{' '}
            <kbd
              style={{
                fontFamily: 'var(--mono, "Courier New", monospace)',
                fontSize: 10,
                background: 'var(--code-bg, #f0ede8)',
                border: '0.5px solid var(--border, #d0ccc4)',
                padding: '1px 5px',
                letterSpacing: 0.5,
              }}
            >
              Ctrl+Shift+D
            </kbd>{' '}
            to open the Debug Panel.
          </span>
        </p>
      </section>
    </main>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 11,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--text-muted, #7a7d8a)',
  fontFamily: 'var(--mono, "Courier New", monospace)',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  border: '0.5px solid var(--border, #d0ccc4)',
  borderRadius: 0,
  background: 'var(--surface, #ffffff)',
  color: 'var(--text, #1a1f2e)',
  textTransform: 'none',
  letterSpacing: 0,
};

const signInButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: 'var(--brand, #1a1f2e)',
  color: '#ffffff',
  fontSize: 14,
  letterSpacing: 0.3,
  border: 'none',
  borderRadius: 0,
  cursor: 'pointer',
  marginTop: 6,
};

// ── WorkOS sign-in splash ───────────────────────────────────────────────────
//
// Public marketing landing for unauthenticated visitors. Anyone hitting the
// SaaS without a session cookie sees this; signing out lands them here too.
// The full splash content lives in @cipherops/ui/SplashScreen (kept in sync
// with the standalone mock at latest/splash-mock.html). This function is a
// thin wrapper that computes the WorkOS sign-in URL (with returnTo) and
// passes it as the sign-in CTA target.

function WorkOSSignInSplash() {
  const path = window.location.pathname + window.location.search + window.location.hash;
  // Reject protocol-relative / backslash targets ("//evil.com", "/\evil.com")
  // that the server guard also blocks — defence in depth against open redirect.
  const safeReturnTo =
    !path || path === '/' || path.startsWith('/auth/') || path.startsWith('//') || path.startsWith('/\\')
      ? '/'
      : path;
  const loginHref = `/auth/login?returnTo=${encodeURIComponent(safeReturnTo)}`;
  return <SplashScreen signInHref={loginHref} />;
}

// ── Generic splash (loading + error states) ─────────────────────────────────

function Splash({ kind, message }: { kind: 'loading' | 'error'; message: string }) {
  const accent = kind === 'error' ? 'var(--sev-high-border, #c44a1a)' : 'var(--text-muted, #7a7d8a)';
  return (
    <main
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        fontFamily: 'var(--sans, system-ui, sans-serif)',
        background: 'var(--bg, #f6f4f0)',
      }}
    >
      <section
        style={{
          maxWidth: 460,
          background: 'var(--surface, #ffffff)',
          border: '0.5px solid var(--border-light, #e0ddd8)',
          padding: '32px 36px',
          borderLeft: `3px solid ${accent}`,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            color: accent,
            marginBottom: 8,
          }}
        >
          {kind === 'error' ? '✗ Error' : '◷ Loading'}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>{message}</div>
        {kind === 'error' && (
          <p style={{ marginTop: 12, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Refresh the page to retry, or contact your firm admin if this persists.
          </p>
        )}
      </section>
    </main>
  );
}
